/**
 * Feature visibility — the global hidden-feature list, plus a per-role overlay.
 *
 * A "feature" here is one of the ids minted by the frontend's featureIds helpers
 * (`nav:/products`, `productTab:specs`, `adminTab:users`, `home:tasks`) or a
 * standalone action id such as `action:generateBarcode`. The global list hides a
 * feature from everyone; the per-role map hides it from the roles it names.
 *
 * ponytail: this gates the UI — nav entries, tabs, home sections, and the route
 * itself via ProtectedRoute — not the API behind it. Hiding `nav:/products` from
 * a role stops that role reaching the page, but `GET /api/v1/products` still
 * answers them; the existing `requireRole` gates remain the data boundary. To
 * make a hidden page's data unreachable too, each route needs its own feature id
 * and a middleware calling `settingsService.hiddenFeaturesFor(req.user.roles)`,
 * the way `barcode.routes.ts` already does for `action:generateBarcode`. Add
 * that per route when a specific one holds data a role must not see — a blanket
 * route→feature mapping table across every route file would be guesswork.
 */
import { normalizeAssignedDivisions } from './division.types.js';
/**
 * Ids every list names, added to `out`. An empty `lists` adds nothing.
 *
 * This is the all-must-hide rule shared by the role and division overlays: a
 * hide bites only when EVERY role (or every assigned division) names it. Roles
 * and divisions are additive, so picking up an extra one can only ever grant
 * access, never take it away. Union semantics would mean handing someone `admin`
 * on top of `viewer` silently kept them locked out of whatever `viewer` was
 * denied — the opposite of what an admin setting permissions expects.
 */
function addSharedHides(out, lists) {
    if (lists.length === 0)
        return;
    const [first, ...rest] = lists;
    for (const id of first) {
        if (rest.every((list) => list.includes(id)))
            out.add(id);
    }
}
/**
 * The effective hidden-feature set for one user.
 *
 * Three overlays, unioned: the global list hides from everyone; the role overlay
 * bites when every role the user holds hides it; the division overlay bites when
 * every division the user is ASSIGNED hides it. A user with no roles is governed
 * by the global and division lists alone; an absent division assignment resolves
 * to `org`, so only what an admin hides from `org` applies to them.
 */
export function resolveHiddenFeatures(global, byRole, roles, byDivision = {}, divisions) {
    const out = new Set(global);
    addSharedHides(out, roles.map((role) => byRole[role] ?? []));
    addSharedHides(out, normalizeAssignedDivisions(divisions).map((id) => byDivision[id] ?? []));
    return [...out];
}
/** Prefix of a nav-entry feature id (`nav:/products`). */
export const NAV_FEATURE_PREFIX = 'nav:';
/**
 * The hidden-feature id for a nav entry, by its route.
 *
 * Lives here rather than only in the frontend because the ids are now read on
 * both sides: a page whose data must follow its nav entry gates its route with
 * the same id, and a second copy of the literal is how a gate quietly stops
 * matching the switch that is supposed to drive it.
 */
export const navFeatureId = (to) => `${NAV_FEATURE_PREFIX}${to}`;
/** The hidden-feature id for an admin-panel tab, by its `value`. */
export const adminTabFeatureId = (value) => `adminTab:${value}`;
/** The hidden-feature id for a product-detail tab, by its `value`. */
export const productTabFeatureId = (value) => `productTab:${value}`;
/**
 * Products Spreadsheet — the first page whose ACCESS, not just its menu entry,
 * is decided by Feature Visibility.
 *
 * It used to carry a hardcoded `roles: ['admin', 'pm']` in the nav config and a
 * matching `requireRole` on its endpoint, which meant an admin could switch it
 * "on" for Marketing and nothing happened: the config is subtract-only, so it
 * could never grant what the role list withheld. The role list is now expressed
 * as per-role hides in the settings document (seeded by migration to exactly
 * the old behaviour), so the switch is the answer rather than being overruled.
 */
export const PRODUCTS_SPREADSHEET_FEATURE = navFeatureId('/products-spreadsheet');
/**
 * Prefix of a Products Spreadsheet column feature id (`spreadsheetColumn:euCost`).
 * Exported so the frontend mints ids and the backend parses them from one
 * constant — a second copy of the literal is exactly how a redaction quietly
 * stops matching the toggle that is supposed to drive it.
 */
export const SPREADSHEET_COLUMN_PREFIX = 'spreadsheetColumn:';
/**
 * The product metadata keys a caller may not be served, derived from their
 * resolved hidden-feature set.
 *
 * This is what turns the spreadsheet-column toggles from a display filter into
 * an access boundary: the same id that removes the column also strips the value
 * from every product response. Without it, hiding "Actual US" only tidies the
 * grid — the figure is still one `GET /api/v1/products/:id` away, which matters
 * the moment an account belongs to someone outside the company.
 */
export function redactedFieldKeys(hidden) {
    return hidden
        .filter((id) => id.startsWith(SPREADSHEET_COLUMN_PREFIX))
        .map((id) => id.slice(SPREADSHEET_COLUMN_PREFIX.length))
        .filter(Boolean);
}
// ─── Spec and evaluation-form fields ────────────────────────────────────────
//
// The third axis, after nav/tabs and spreadsheet columns: one toggle per field
// of a spec module or an evaluation form. Same document, same resolver, same
// additive per-role semantics — only the id shape differs.
//
// The id embeds the owning module/form because a field path is only unique
// within one of them (`weight` exists in several spec modules). Nothing ever
// splits an id back apart: both sides compose the id and test membership, so a
// path containing a dot is not ambiguous.
export const SPEC_FIELD_PREFIX = 'specField:';
/** The hidden-feature id for one field of one spec module. */
export const specFieldFeatureId = (moduleId, fieldPath) => `${SPEC_FIELD_PREFIX}${moduleId}.${fieldPath}`;
export const EVAL_FIELD_PREFIX = 'evalField:';
/** The hidden-feature id for one field of one evaluation form definition. */
export const evalFieldFeatureId = (formId, fieldPath) => `${EVAL_FIELD_PREFIX}${formId}.${fieldPath}`;
/** True when the set gates at least one field of this kind — a cheap early out. */
export function gatesAnyField(hidden, prefix) {
    return hidden.some((id) => id.startsWith(prefix));
}
/**
 * Spec module definitions with the caller's gated fields removed.
 *
 * The definitions drive what the Specs tab renders, so dropping a field here is
 * what takes the row off the page; the value is stripped from the spec document
 * server-side regardless, which is the actual boundary.
 */
export function filterSpecModuleFields(modules, hidden) {
    if (!gatesAnyField(hidden, SPEC_FIELD_PREFIX))
        return [...modules];
    const gated = new Set(hidden);
    return modules.map((m) => ({
        ...m,
        fields: (m.fields ?? []).filter((f) => !gated.has(specFieldFeatureId(m.moduleId, f.path))),
    }));
}
/**
 * Form sections with the caller's gated fields removed, subsections included.
 *
 * A section left with no fields AND no surviving subsections is dropped too —
 * an empty heading advertises exactly what was taken away. Shared because the
 * server filters the schema it resolves for a submission and the client filters
 * the definition it renders a draft from; two copies would drift.
 */
export function filterEvalSections(sections, formId, hidden) {
    if (!gatesAnyField(hidden, EVAL_FIELD_PREFIX))
        return [...sections];
    const gated = new Set(hidden);
    const walk = (list) => list
        .map((s) => ({
        ...s,
        fields: (s.fields ?? []).filter((f) => !gated.has(evalFieldFeatureId(formId, f.path))),
        subsections: s.subsections ? walk(s.subsections) : s.subsections,
    }))
        .filter((s) => s.fields.length > 0 || (s.subsections?.length ?? 0) > 0);
    return walk(sections);
}
/** Answers with the caller's gated fields removed. Keys are field paths. */
export function redactEvalAnswers(data, formId, hidden) {
    if (!gatesAnyField(hidden, EVAL_FIELD_PREFIX))
        return data;
    const gated = new Set(hidden);
    const out = {};
    for (const [key, value] of Object.entries(data)) {
        if (!gated.has(evalFieldFeatureId(formId, key)))
            out[key] = value;
    }
    return out;
}
// ─── Write scopes (subtract-only) ───────────────────────────────────────────
//
// The second axis of Feature Visibility: not "can this role SEE the section"
// but "can it CHANGE anything in it". Stored as ordinary hidden-feature ids
// (`write:products`), so there is one config document, one resolver and one
// endpoint rather than a parallel permission system.
//
// SUBTRACT-ONLY, and this is the property to preserve: the existing role gates
// (`requireRole`, `requireInternalWriter`) remain the floor, and a switch here
// can only take write away. A missing, empty or corrupt config therefore denies
// nothing it should have granted — it simply leaves the hardcoded rules in
// force. Never invert this into "the config grants access", or a bad write to
// the settings document starts handing out permissions instead of removing them.
/** Sections that have something to write. Ordered as the admin page lists them. */
export const WRITE_SCOPES = [
    'products',
    'specs',
    'productAssets',
    'sharepoint',
    'evaluations',
    'bugs',
    'shipments',
    'bulletins',
    'help',
    'priceList',
];
export const WRITE_SCOPE_LABELS = {
    products: 'Products',
    specs: 'Product Specs',
    productAssets: 'Product Assets',
    sharepoint: 'SharePoint Files',
    evaluations: 'Evaluations',
    bugs: 'Bug Tracker',
    shipments: 'Shipments',
    bulletins: 'Bulletins',
    help: 'Help articles',
    priceList: 'Price List',
};
/** What switching one off actually stops, in the words an admin needs. */
export const WRITE_SCOPE_DESCRIPTIONS = {
    products: 'Create, edit, publish and delete products, and edit their PDLC tasks. Also covers everything filed under a product, so switching this off stops spec and asset changes too.',
    specs: 'Save, publish and import product specifications, and edit spec modules.',
    productAssets: 'Upload, replace, rename and delete product files, and share links to them.',
    sharepoint: 'Upload files and create folders in a product\u2019s linked SharePoint folder, from the SharePoint tab. Browsing and downloading stay available either way \u2014 switch the tab itself off to stop those.',
    evaluations: 'Create and edit evaluations, submit, lock and unlock them, and edit eval forms.',
    bugs: 'Raise bugs and tech-writing requests from ATLAS.',
    shipments: 'Create and edit shipments, and mark one received.',
    bulletins: 'Compose, schedule and send bulletins, and manage brands and lists.',
    help: 'Write, publish and archive help articles and categories.',
    priceList: 'Edit prices and other columns from the Price List page. Switching this off leaves the page read-only for the role; downloads still work.',
};
/** The hidden-feature id that switches write off for a scope. */
export const writeFeatureId = (scope) => `write:${scope}`;
/**
 * Whether a caller may write to `scope`, given their resolved hidden set.
 *
 * Read as: permitted unless explicitly switched off. The caller must ALSO pass
 * whatever role gate the route already had — this never grants, only removes.
 */
export function canWriteScope(scope, hidden) {
    return !hidden.includes(writeFeatureId(scope));
}
//# sourceMappingURL=feature-visibility.js.map